import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Words, FadeUp, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";
import { EPISODES } from "@/data/episodes";

const Episodes = () => {
  const [sort, setSort] = useState("latest");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    let l = [...EPISODES];
    if (sort === "oldest") l.reverse();
    const q = query.trim().toLowerCase();
    if (q) {
      l = l.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          e.description.toLowerCase().includes(q) ||
          e.track.toLowerCase().includes(q) ||
          (e.with || []).some((w) => w.toLowerCase().includes(q))
      );
    }
    return l;
  }, [sort, query]);

  return (
    <main className="bg-white">
      <section className="w-full bg-white pt-[110px] sm:pt-[130px] lg:pt-[150px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-5 lg:px-[60px]">
            <Words text="The Podcast" mode="load" delay={0.1} className="eyebrow inline-flex overflow-hidden text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]" />
            <h1 className="font-display text-[36px] leading-[1.08] text-black sm:text-[48px] lg:text-[58px] lg:leading-[64px]" aria-label="Watch the Latest Conversations">
              <span className="block">
                <Words text="Watch the Latest" mode="load" delay={0.2} stagger={0.07} />
              </span>
              <span className="block">
                <Words text="Conversations" mode="load" delay={0.38} stagger={0.07} />
              </span>
            </h1>
            <FadeUp mode="load" delay={0.55}>
              <p className="max-w-[620px] text-[15px] leading-[26px] text-charcoal sm:text-[15.6px] sm:leading-[27.2px]">
                Two tracks, one table. Testimony Tuesday brings honest first-person stories of what God has done. The Faith &amp; Politics track opens wider
                conversations on culture, country, and conviction. Newest on top — sort or search to find a specific episode.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="w-full bg-white py-14 sm:py-16 lg:py-[72px]">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-10 lg:px-[60px]">
            <div className="flex flex-col items-start justify-between gap-5 border-b border-hairline pb-6 md:flex-row md:items-end">
              <div className="flex flex-col gap-2">
                <FadeUp y={14}>
                  <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[30px]">
                    <Words text="Every Conversation, In One Place" />
                  </h2>
                </FadeUp>
              </div>
              <FadeUp y={12} className="flex w-full flex-col gap-3 sm:flex-row sm:items-center md:w-auto">
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search episodes…"
                  data-testid="episodes-search-input"
                  aria-label="Search episodes"
                  className="h-11 w-full rounded-full border border-hairline bg-white-smoke px-5 text-[14px] text-ink outline-none transition-colors placeholder:text-charcoal-mid focus:border-silver sm:w-[240px]"
                />
                <label className="relative inline-flex items-center">
                  <span className="sr-only">Sort episodes</span>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="episodes-sort-select"
                    className="h-11 w-full cursor-pointer appearance-none rounded-full border border-hairline bg-white-smoke pl-5 pr-10 text-[14px] text-charcoal outline-none transition-colors focus:border-silver sm:w-auto"
                  >
                    <option value="latest">Latest first</option>
                    <option value="oldest">Oldest first</option>
                  </select>
                  <span aria-hidden="true" className="pointer-events-none absolute right-4 text-charcoal">
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </label>
              </FadeUp>
            </div>

            <div className="flex flex-col divide-y divide-silver-line" data-testid="episodes-list">
              {list.map((ep, i) => (
                <motion.article
                  key={ep.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, ease: EASE, delay: 0.04 * (i % 3) }}
                  className="grid grid-cols-1 items-center gap-8 py-8 md:grid-cols-[1fr_300px] md:gap-10 lg:py-10"
                  data-testid={`episodes-row-${ep.id}`}
                >
                  <div className="flex flex-col gap-3">
                    <FadeUp y={10}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="eyebrow text-[10.5px] leading-[16px] text-charcoal-mid">Episode {ep.number}</span>
                        <span aria-hidden="true" className="h-1 w-1 rounded-full bg-silver" />
                        <span className="text-[12.5px] leading-[18px] text-charcoal-mid">{ep.date}</span>
                        <span aria-hidden="true" className="h-1 w-1 rounded-full bg-silver" />
                        <span className="text-[12.5px] leading-[18px] text-charcoal-mid">{ep.duration}</span>
                        <span aria-hidden="true" className="h-1 w-1 rounded-full bg-silver" />
                        <span className="text-[12.5px] leading-[18px] text-charcoal-mid">{ep.type}</span>
                        {ep.comingSoon && (
                          <span className="ml-1 inline-flex items-center rounded-full border border-hairline bg-white-smoke px-3 py-[2px] text-[10.5px] uppercase leading-[16px] tracking-[0.22em] text-charcoal-mid font-display">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </FadeUp>
                    <h3 className="font-display text-[26px] leading-[1.15] text-black transition-transform duration-500 group-hover:-translate-x-[2px] sm:text-[30px] lg:text-[33px]">
                      <Words text={ep.title} stagger={0.035} />
                    </h3>
                    <FadeUp y={14}>
                      <p className="max-w-[620px] text-[14.4px] leading-[25px] text-charcoal sm:text-[14.6px]">{ep.description}</p>
                    </FadeUp>
                    {ep.with && ep.with.length > 0 && (
                      <FadeUp y={10}>
                        <p className="text-[12.5px] leading-[20px] text-charcoal-mid">With {ep.with.join(" · ")}</p>
                      </FadeUp>
                    )}
                    <FadeUp y={10} className="mt-1 flex flex-wrap items-center gap-5">
                      <Link to={`/episodes/${ep.id}`} data-testid={`episodes-open-${ep.id}`} className="group/link inline-flex items-center font-display text-[14.5px] leading-[24px] text-charcoal transition-colors hover:text-black">
                        <span className="transition-transform duration-500 ease-out group-hover/link:-translate-x-1">Open episode</span>
                        <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover/link:translate-x-1">→</span>
                      </Link>
                      <Link to={`/episodes/${ep.id}#show-notes`} data-testid={`episodes-notes-${ep.id}`} className="font-display text-[14.5px] leading-[24px] text-charcoal underline decoration-silver underline-offset-4 transition-colors hover:text-black hover:decoration-black">
                        Read show notes
                      </Link>
                    </FadeUp>
                  </div>
                  <motion.div
                    initial={{ clipPath: i % 2 === 0 ? "inset(0% 100% 0% 0%)" : "inset(0% 0% 0% 100%)" }}
                    whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="group relative aspect-[300/225] w-full overflow-hidden rounded-2xl"
                  >
                    <div className="absolute inset-0 transition-transform duration-[1100ms] group-hover:scale-[1.06]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                      <img alt={ep.title} className="h-full w-full object-cover" src={ep.image} />
                    </div>
                    <div className="pointer-events-none absolute inset-0 bg-linen opacity-15 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0" />
                  </motion.div>
                </motion.article>
              ))}
              {list.length === 0 && (
                <div className="py-16 text-center">
                  <p className="font-display text-[22px] text-charcoal">No episodes match “{query}.”</p>
                  <p className="mt-2 text-[14px] text-charcoal-mid">Try a different word — or browse everything by clearing the search.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-white-smoke py-16 sm:py-20">
        <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[56px]">
          <div className="mx-auto flex max-w-[1208px] flex-col items-start gap-6 rounded-3xl border border-hairline bg-white p-8 sm:p-12 lg:flex-row lg:items-center lg:justify-between lg:px-[60px]">
            <div className="flex max-w-[560px] flex-col gap-3">
              <h2 className="font-display text-[26px] leading-[1.15] text-black sm:text-[32px]">
                <Words text="Join the community for new drops" />
              </h2>
              <FadeUp y={14}>
                <p className="text-[14.6px] leading-[26px] text-charcoal sm:text-[15.2px]">
                  A short note when a new conversation lands — Testimony Tuesday or the Faith &amp; Politics track. No newsletter, no noise.
                </p>
              </FadeUp>
            </div>
            <FadeUp y={12} className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <PillButton href="/community" dataTestId="episodes-join-button">
                Join the Community
              </PillButton>
              <PillButton href="/about" variant="outline" dataTestId="episodes-about-button">
                About Stephanie
              </PillButton>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Episodes;
